import { Link } from "react-router-dom";
import type { DashboardInstance, DashboardSpend } from "../../../pages/dashboard/mock";
import { formatCount, windowPeriod, type InstanceLimit } from "../../../lib/toolUsage";
import { Skeleton } from "../Skeleton";
import styles from "./KpiStrip.module.css";

const Empty = (): JSX.Element => <span className={styles.empty} aria-label="not yet available">—</span>;

type KpiStripProps = {
  readonly instance: DashboardInstance;
  readonly spend: DashboardSpend | null;
  // When present, renders the gear on the AI cell. It opens AI settings — key
  // rotation, provider switch — rather than any one action, so this cell has a
  // single affordance however many things are configurable. Visibility (and
  // what the panel offers) is decided by the parent.
  readonly onOpenAiSettings?: () => void;
  // True while the instance is being asked which account it actually bills.
  // The address below comes from our tenant row, which lags a switch by the
  // length of that read (~36s in prod), so it must not be shown as settled.
  readonly accountChecking?: boolean;
  // The billed account as the switch dialog just reported it, standing in
  // until our tenant row catches up on the next claws snapshot. Newer than the
  // row by definition, so it wins whenever it is set.
  readonly accountOverride?: string | null;
  // The instance-wide tool-call window closest to pausing; omitted hides the cell.
  readonly toolCalls?: { limit: (InstanceLimit & { pct: number }) | null; paused: boolean; href: string };
};

const GearIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const formatUsd = (n: number): string =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fillState = (pct: number): "calm" | "warn" | "alert" =>
  pct >= 90 ? "alert" : pct >= 70 ? "warn" : "calm";

export const KpiStrip = ({
  instance,
  spend,
  onOpenAiSettings,
  accountChecking = false,
  accountOverride = null,
  toolCalls,
}: KpiStripProps): JSX.Element => {
  const isProvisioning = instance.status === "provisioning";
  const onClaude = instance.aiMode === "claude";
  const onChatGpt = !onClaude && (instance.aiMode === "chatgpt" || instance.chatgptLinked === true || accountOverride != null);
  const billedAccount = accountOverride ?? instance.chatgptAccount;

  const hasDisk =
    instance.diskUsedGb !== undefined && instance.diskTotalGb !== undefined;
  const diskPct: number = hasDisk
    ? (instance.diskUsedGb! / instance.diskTotalGb!) * 100
    : 0;

  const hasAiLimit = spend !== null && spend.limitUsd !== null;
  const aiPct: number = hasAiLimit
    ? Math.min(100, (spend!.spentUsd / (spend!.limitUsd as number)) * 100)
    : 0;

  return (
    <dl className={styles.strip}>
      <div className={styles.cell}>
        <dt className={styles.label}>// STATUS</dt>
        <dd className={styles.value}>
          <span
            className={styles.healthDot}
            data-state={isProvisioning ? "provisioning" : "running"}
            aria-hidden="true"
          />
          <span className={styles.num}>
            {isProvisioning ? "Provisioning" : "Healthy"}
          </span>
          {!isProvisioning && instance.uptimeDays > 0 ? (
            <span className={styles.unit}>
              · {instance.uptimeDays} day{instance.uptimeDays === 1 ? "" : "s"}
            </span>
          ) : null}
        </dd>
      </div>

      <div className={styles.divider} aria-hidden="true" />

      <div className={styles.cell}>
        <dt className={styles.label}>// DISK</dt>
        <dd className={styles.value}>
          {hasDisk ? (
            <>
              <span className={styles.num}>{instance.diskUsedGb!.toFixed(1)}</span>
              <span className={styles.unit}>/ {instance.diskTotalGb!.toFixed(0)} GB</span>
            </>
          ) : (
            <Empty />
          )}
        </dd>
        {hasDisk ? (
          <div className={styles.diskTrack} data-state={fillState(diskPct)}>
            <span
              className={styles.diskFill}
              style={{ width: `${diskPct.toFixed(0)}%` }}
            />
          </div>
        ) : null}
      </div>

      <div className={styles.divider} aria-hidden="true" />

      <div className={styles.cell}>
        <dt className={styles.labelRow}>
          <span className={styles.label}>// AI {onChatGpt || onClaude ? "PROVIDER" : "CREDITS"}</span>
          {onOpenAiSettings !== undefined ? (
            <button
              type="button"
              className={styles.gearBtn}
              onClick={onOpenAiSettings}
              aria-label="AI settings"
              title="AI settings"
              data-testid="ai-settings-gear"
            >
              <GearIcon />
            </button>
          ) : null}
        </dt>
        <dd className={styles.value}>
          {/* On a ChatGPT subscription the OpenRouter spend figure is no longer
              what the agent costs — showing it would read as "you're still
              paying per token on top of your plan". */}
          {onClaude ? (
            <>
              <span className={styles.num} data-testid="kpi-claude">
                Claude
              </span>
              <span className={styles.unit}>Subscription</span>
            </>
          ) : onChatGpt ? (
            <>
              <span className={styles.num} data-testid="kpi-chatgpt">
                ChatGPT
              </span>
              {/* Whose plan is paying. "ChatGPT plan" alone left the obvious
                  question unanswered on a shared instance. The stored address
                  is only as fresh as the last instance read, so a switch either
                  hands us the new one (accountOverride) or, where it can't, a
                  placeholder holds the slot rather than the previous account:
                  an operator who had just moved the billing read that stale
                  value as the change having failed. */}
              {!instance.chatgptLinked && accountOverride == null ? (
                <span className={`${styles.unit} ${styles.account}`} title="Connect ChatGPT to finish setup" data-testid="kpi-chatgpt-unlinked">
                  Not connected
                </span>
              ) : accountChecking ? (
                <span
                  className={styles.unit}
                  role="status"
                  aria-label="Checking which ChatGPT account is billed"
                  data-testid="kpi-chatgpt-account-checking"
                >
                  <Skeleton width="9rem" height="0.75rem" />
                </span>
              ) : billedAccount != null ? (
                <span className={`${styles.unit} ${styles.account}`} title={billedAccount}>
                  {billedAccount}
                </span>
              ) : null}
            </>
          ) : spend !== null ? (
            <>
              <span className={styles.num}>{formatUsd(spend.spentUsd)}</span>
              {spend.limitUsd !== null ? (
                <span className={styles.unit}>
                  / {formatUsd(spend.limitUsd)} · {spend.periodLabel}
                </span>
              ) : (
                <span className={styles.unit}>{spend.periodLabel} · no limit</span>
              )}
            </>
          ) : (
            <Empty />
          )}
        </dd>
        {hasAiLimit && !onChatGpt && !onClaude ? (
          <div className={styles.diskTrack} data-state={fillState(aiPct)}>
            <span
              className={styles.diskFill}
              style={{ width: `${aiPct.toFixed(0)}%` }}
            />
          </div>
        ) : null}
      </div>
      {toolCalls !== undefined ? (
        <>
          <div className={styles.divider} aria-hidden="true" />
          <div className={styles.cell} data-testid="kpi-tool-calls">
            <dt className={styles.label}>
              <Link to={toolCalls.href} className={styles.cellLink}>
                // TOOL CALLS
              </Link>
            </dt>
            <dd className={styles.value}>
              {toolCalls.limit !== null ? (
                <>
                  <span className={styles.num}>{formatCount(toolCalls.limit.used)}</span>
                  <span className={styles.unit}>
                    / {formatCount(toolCalls.limit.pausesAt ?? 0)} · {windowPeriod(toolCalls.limit.window)}
                  </span>
                </>
              ) : (
                <Empty />
              )}
            </dd>
            {toolCalls.limit !== null ? (
              <div className={styles.diskTrack} data-state={toolCalls.paused ? "alert" : fillState(toolCalls.limit.pct)}>
                <span className={styles.diskFill} style={{ width: `${toolCalls.limit.pct.toFixed(0)}%` }} />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </dl>
  );
};
